import { ClientRegistrationState } from '@common/model/client/registration';
import { IAuthService } from '@cp/shared/auth';
import { IRegistrationApiProvider, IRegistrationServiceStorage } from '../lib';
import { FlowStep, generateRegistrationFlow } from './flow';
import { FieldList, RegistrationStep } from './step';

export type GeneratorStep = {
  done?: boolean;
  value: FlowStep;
};

export class RegistrationService {
  private flow: ReturnType<typeof generateRegistrationFlow>;
  private step: GeneratorStep | undefined;
  private isRegistered = false;
  private _isReady = false;

  get isReady() {
    return this._isReady;
  }

  constructor(
    private readonly apiProvider: IRegistrationApiProvider,
    private readonly storage: IRegistrationServiceStorage,
    private readonly authService: IAuthService,
    private readonly options: { onFlowComplete?: (payload: { state: Partial<ClientRegistrationState> }) => void | Promise<void> } = {},
  ) {}

  async init(token: string, initialState: Partial<ClientRegistrationState> = {}, fields?: FieldList) {
    if (this._isReady) {
      return;
    }
    this.storage.setToken(token);
    const savedState = this.storage.getState();
    const state = { ...savedState, ...initialState };

    if (!fields) {
      const res = await this.request(state);
      this.storage.setToken(res.registrationSessionId);
      this.storage.setState(res.state);
      if (res.token) {
        this.authService.authorize(res.token);
      }
      if (res.isRegistered) {
        this.startFlow(res.state, []);
        await this.onFlowComplete();
        return;
      }
      if (!res.remainingFields) {
        throw new Error('Client registration returned no fields nor registered');
      }
      this.startFlow(res.state, res.remainingFields);
    } else {
      this.startFlow(state, fields);
    }
    this._isReady = true;
  }

  getStep() {
    if (!this.step) {
      this.step = this.flow.next();
    }
    return this.step;
  }

  async completeStep(state: Partial<ClientRegistrationState>) {
    // Where redirect to separate page
    if (!this.step?.value.step) {
      this.step = this.flow.next();
    }
    if (this.isRegistered) {
      this.completeRegisteredStep();
    } else {
      await this.completeUnregisteredStep(state);
    }
    if (this.step.done) {
      await this.onFlowComplete();
      return this.step;
    }
    return this.step;
  }

  private completeRegisteredStep() {
    this.step = this.flow.next();
  }

  private async completeUnregisteredStep(state: Partial<ClientRegistrationState>) {
    const response = await this.request(state);
    this.isRegistered = response.isRegistered;
    if (this.step?.value.step && this.isRequiredFieldsCompleted(this.step.value.step, response.remainingFields)) {
      this.step = this.flow.next({ fields: response.remainingFields || [], state: response.state });
    }
    this.storage.setState(response.state);
    this.storage.setToken(response.registrationSessionId);
    if (response.token) {
      this.authService.authorize(response.token);
    }
  }

  private async onFlowComplete() {
    const state = this.storage.getState();

    await this.options.onFlowComplete?.({ state });

    this.storage.clear();
  }

  private isRequiredFieldsCompleted(step: RegistrationStep, remainFields: FieldList = []): boolean {
    for (const field of step.fields.filter((fieldConf) => Boolean(fieldConf.required))) {
      if (remainFields.includes(field.name)) {
        return false;
      }
    }
    return true;
  }

  private request(state: Partial<ClientRegistrationState>) {
    const registrationSessionId = this.storage.getToken();
    return this.apiProvider.register({ registrationSessionId, state });
  }

  private startFlow(state: Partial<ClientRegistrationState>, fields: FieldList) {
    this.flow = generateRegistrationFlow(state, fields);
  }
}
